import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, FileText, CheckCircle, Phone, Loader2, Bot, User, ArrowLeft } from 'lucide-react';

/**
 * ConversationMode - Interface de chat IA moderne et responsive
 */
const ConversationMode = ({ 
  formSlug, 
  backendUrl, 
  onSwitchToClassic, 
  onSubmitSuccess,
  collectedDataRef,
  formName
}) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('active');
  const [collectedData, setCollectedData] = useState({});
  const [showRecap, setShowRecap] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll automatique
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    startConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSlug]);

  useEffect(() => {
    if (collectedDataRef) {
      collectedDataRef.current = collectedData;
    }
  }, [collectedData, collectedDataRef]);

  const startConversation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/ai/conversation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_slug: formSlug })
      });

      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.session_id);
        setMessages([{ role: 'assistant', content: data.message, timestamp: new Date() }]);
      } else {
        setError(data.detail || "Impossible de démarrer la conversation");
      }
    } catch (err) {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || sending || !sessionId) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setSending(true);

    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);

    try {
      const response = await fetch(`${backendUrl}/api/ai/conversation/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMessage })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message, timestamp: new Date() }]);
        setCollectedData(data.collected_data || {});
        setStatus(data.status);
        if (data.all_collected) setShowRecap(true);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "Désolé, j'ai rencontré un problème. Pouvez-vous reformuler ?",
          isError: true,
          timestamp: new Date()
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Erreur de connexion. Veuillez réessayer.",
        isError: true,
        timestamp: new Date()
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitConversation = async () => {
    setSending(true);
    try {
      const response = await fetch(`${backendUrl}/api/ai/conversation/${sessionId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('submitted');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "✨ Parfait ! Votre demande a été enregistrée avec succès. Nous vous recontacterons très rapidement.",
          isSuccess: true,
          timestamp: new Date()
        }]);
        if (onSubmitSuccess) onSubmitSuccess(data.submission_id, collectedData);
      } else {
        setError(data.detail || "Erreur lors de la soumission");
      }
    } catch (err) {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px]" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #fae8ff 50%, #fdf4ff 100%)' }}>
        <div className="relative">
          <div className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' }}>
            <Bot className="w-10 h-10 text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-400 rounded-full border-4 border-white animate-bounce"></div>
        </div>
        <p className="mt-6 text-gray-700 font-medium">Initialisation de l'assistant...</p>
        <p className="text-sm text-gray-500 mt-1">Quelques secondes...</p>
      </div>
    );
  }

  // Error state
  if (error && !sessionId) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <MessageSquare className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Mode conversation indisponible</h3>
        <p className="text-gray-500 mb-6 max-w-sm">{error}</p>
        <button
          onClick={onSwitchToClassic}
          className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-all shadow-lg flex items-center gap-2 font-medium"
        >
          <FileText size={18} />
          Utiliser le formulaire classique
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 50%, #faf5ff 100%)' }}>
      {/* Header avec gradient violet */}
      <div 
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 50%, #c026d3 100%)' }}
      >
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative">
                <div 
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}
                >
                  <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h3 className="font-bold text-white text-base sm:text-lg">Assistant IA</h3>
                <p className="text-white/70 text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">{formName || 'Formulaire'}</p>
              </div>
            </div>
            
            <button
              onClick={onSwitchToClassic}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-white text-xs sm:text-sm font-medium transition-all border border-white/30"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <ArrowLeft size={14} />
              <span className="hidden sm:inline">Formulaire</span>
              <FileText size={14} className="sm:hidden" />
            </button>
          </div>
        </div>
      </div>

      {/* Zone de messages */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            style={{ 
              animation: 'fadeInUp 0.3s ease-out forwards',
              animationDelay: `${idx * 50}ms`
            }}
          >
            {/* Avatar IA */}
            {msg.role !== 'user' && (
              <div className="flex-shrink-0 mr-2 sm:mr-3">
                <div 
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center"
                  style={{ 
                    background: msg.isError ? '#fee2e2' : msg.isSuccess ? '#dcfce7' : 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)'
                  }}
                >
                  {msg.isError ? (
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                  ) : msg.isSuccess ? (
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  ) : (
                    <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  )}
                </div>
              </div>
            )}
            
            {/* Bulle de message */}
            <div
              className="max-w-[80%] sm:max-w-[75%] px-4 sm:px-5 py-3 sm:py-4"
              style={{
                borderRadius: msg.role === 'user' ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
                background: msg.role === 'user' 
                  ? 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)' 
                  : msg.isError 
                  ? '#fef2f2' 
                  : msg.isSuccess 
                  ? 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
                  : '#ffffff',
                color: msg.role === 'user' ? '#ffffff' : msg.isError ? '#991b1b' : msg.isSuccess ? '#166534' : '#1e293b',
                boxShadow: msg.role === 'user' 
                  ? '0 4px 15px rgba(124, 58, 237, 0.3)' 
                  : '0 2px 10px rgba(0,0,0,0.08)',
                border: msg.role !== 'user' && !msg.isError && !msg.isSuccess ? '1px solid #e2e8f0' : 'none'
              }}
            >
              <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              <p 
                className="text-xs mt-2"
                style={{ color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}
              >
                {msg.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            
            {/* Avatar utilisateur */}
            {msg.role === 'user' && (
              <div className="flex-shrink-0 ml-2 sm:ml-3">
                <div 
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)' }}
                >
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Indicateur de frappe */}
        {sending && (
          <div className="flex justify-start">
            <div className="flex-shrink-0 mr-2 sm:mr-3">
              <div 
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)' }}
              >
                <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <div 
              className="px-5 py-4"
              style={{ 
                borderRadius: '20px 20px 20px 6px',
                background: '#ffffff',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                border: '1px solid #e2e8f0'
              }}
            >
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#8b5cf6', animationDelay: '0ms' }}></span>
                <span className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#8b5cf6', animationDelay: '150ms' }}></span>
                <span className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ background: '#8b5cf6', animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Récapitulatif et confirmation */}
      {showRecap && status !== 'submitted' && (
        <div className="p-4 sm:p-6 border-t border-green-200" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-green-800">Toutes les informations collectées</span>
              <p className="text-xs text-green-600">Prêt à envoyer votre demande</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={submitConversation}
              disabled={sending}
              className="flex-1 px-5 py-3 text-white rounded-xl transition-all font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)', boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send size={18} />}
              Envoyer ma demande
            </button>
            <button
              onClick={() => setShowRecap(false)}
              className="px-5 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all border border-gray-200 font-medium"
            >
              Continuer
            </button>
          </div>
        </div>
      )}

      {/* Demande de rappel */}
      {status === 'callback_requested' && (
        <div className="p-4 sm:p-6 border-t border-amber-200" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Phone className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <span className="font-semibold text-amber-800">Un conseiller va vous rappeler</span>
              <p className="text-xs text-amber-600">Nous vous contacterons rapidement</p>
            </div>
          </div>
          <button
            onClick={submitConversation}
            disabled={sending}
            className="w-full px-5 py-3 text-white rounded-xl transition-all font-semibold disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)' }}
          >
            {sending ? 'Enregistrement...' : 'Confirmer mes coordonnées'}
          </button>
        </div>
      )}

      {/* Succès final */}
      {status === 'submitted' && (
        <div className="p-6 sm:p-8 border-t border-green-200 text-center" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)' }}>
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)', boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
          >
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-xl font-bold text-green-800 mb-2">Demande enregistrée !</h4>
          <p className="text-sm text-green-600">Nous vous recontacterons rapidement.</p>
        </div>
      )}

      {/* Zone de saisie */}
      {status !== 'submitted' && status !== 'callback_requested' && !showRecap && (
        <div className="p-4 sm:p-5 border-t border-gray-200 bg-white/90">
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Écrivez votre message..."
              disabled={sending}
              className="flex-1 px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:bg-white disabled:bg-gray-100 transition-all text-sm sm:text-base"
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || sending}
              className="px-4 sm:px-5 py-3 sm:py-4 text-white rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
                boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)'
              }}
            >
              {sending ? (
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
              ) : (
                <Send className="w-5 h-5 sm:w-6 sm:h-6" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center hidden sm:block">
            Propulsé par l'IA • Vos données sont sécurisées
          </p>
        </div>
      )}

      {/* Styles d'animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ConversationMode;
